import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import { QRCodeSVG } from "qrcode.react";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import { useAuth } from "auth/AuthContext";

import bgImage from "assets/images/bg-sign-in-basic.jpeg";

/** Khoá bí mật chia nhóm 4 ký tự cho dễ gõ tay vào ứng dụng. */
function groupSecret(secret) {
  return (secret || "").replace(/(.{4})/g, "$1 ").trim();
}

function Login() {
  const { admin, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(true);
  const [code, setCode] = useState("");
  // null: bước mật khẩu; { needTotp } hoặc { needTotpSetup, secret, uri }: bước mã xác thực.
  const [step, setStep] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const target =
    location.state?.from && location.state.from !== "/login" ? location.state.from : "/dashboard";

  if (admin) {
    return <Navigate to={target} replace />;
  }

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const next = await login(username.trim(), password, remember, step ? code : undefined);
      if (next) {
        setStep(next);
        setCode("");
        return;
      }
      navigate(target, { replace: true });
    } catch (e) {
      setError(e.message);
      setCode("");
    } finally {
      setBusy(false);
    }
  };

  const back = () => {
    setStep(null);
    setCode("");
    setError(null);
  };

  const codeReady = code.replace(/\D/g, "").length === 6;

  return (
    <MDBox
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={2}
      sx={({ functions: { linearGradient, rgba }, palette: { gradients } }) => ({
        backgroundImage: `${linearGradient(
          rgba(gradients.dark.main, 0.6),
          rgba(gradients.dark.state, 0.6)
        )}, url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      })}
    >
      <Card sx={{ width: "100%", maxWidth: 400 }}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Pet Realm Admin
          </MDTypography>
          <MDTypography variant="button" color="white" opacity={0.8}>
            {step ? "Xác thực 2 lớp" : "Đăng nhập bằng tài khoản có quyền admin trong game"}
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3} component="form" role="form" onSubmit={submit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {!step && (
            <>
              <MDBox mb={2}>
                <MDInput
                  label="Tài khoản"
                  fullWidth
                  autoFocus
                  autoComplete="username"
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </MDBox>
              <MDBox mb={2}>
                <MDInput
                  type="password"
                  label="Mật khẩu"
                  fullWidth
                  autoComplete="current-password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </MDBox>
              <FormControlLabel
                control={
                  <Switch
                    checked={remember}
                    onChange={(event) => setRemember(event.target.checked)}
                  />
                }
                label="Duy trì đăng nhập"
              />
            </>
          )}
          {step?.needTotpSetup && (
            <MDBox mb={2}>
              <MDTypography variant="button" color="text" display="block" mb={1.5}>
                Bật xác thực 2 lớp một lần: mở Google Authenticator (hoặc Authy, Microsoft
                Authenticator) trên điện thoại, chọn thêm tài khoản rồi quét mã QR. Sau đó vào được
                web admin từ mọi mạng, không cần cấp lại IP.
              </MDTypography>
              <MDBox display="flex" justifyContent="center" mb={1.5}>
                <MDBox p={1.5} bgColor="white" borderRadius="md" lineHeight={0}>
                  <QRCodeSVG value={step.uri} size={184} level="M" />
                </MDBox>
              </MDBox>
              <MDTypography variant="caption" color="text" display="block" textAlign="center">
                Không quét được thì nhập khoá này vào ứng dụng:
              </MDTypography>
              <MDTypography
                variant="button"
                fontWeight="bold"
                display="block"
                textAlign="center"
                sx={{ fontFamily: "monospace", wordBreak: "break-all", userSelect: "all" }}
              >
                {groupSecret(step.secret)}
              </MDTypography>
            </MDBox>
          )}
          {step && (
            <MDBox mb={2}>
              {step.needTotp && (
                <MDTypography variant="button" color="text" display="block" mb={1.5}>
                  Nhập mã 6 số đang hiện trong ứng dụng xác thực cho tài khoản {username.trim()}.
                </MDTypography>
              )}
              <MDInput
                label="Mã 6 số"
                fullWidth
                autoFocus
                autoComplete="one-time-code"
                inputProps={{ inputMode: "numeric", maxLength: 7 }}
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/[^\d ]/g, ""))}
              />
            </MDBox>
          )}
          <MDBox mt={3} mb={1}>
            <MDButton
              type="submit"
              variant="gradient"
              color="info"
              fullWidth
              disabled={busy || (step ? !codeReady : !username.trim() || !password)}
            >
              {busy && "Đang kiểm tra..."}
              {!busy && !step && "Đăng nhập"}
              {!busy && step?.needTotpSetup && "Bật xác thực 2 lớp và đăng nhập"}
              {!busy && step?.needTotp && "Xác nhận"}
            </MDButton>
          </MDBox>
          {step && (
            <MDButton variant="text" color="secondary" fullWidth onClick={back} disabled={busy}>
              Quay lại
            </MDButton>
          )}
        </MDBox>
      </Card>
    </MDBox>
  );
}

export default Login;
